import { useMemo } from "react";
import { Zap } from "lucide-react";
import { compute, STRESS } from "@/lib/finance";
import { usd, pctFrac, mult } from "@/lib/format";
import { Card } from "@/components/common/Ornament";

const ROWS = [
  ["Total Project Cost", (r) => usd(r.totalCost)],
  ["Cost per Key", (r) => usd(r.costPerKey)],
  ["Yield on Cost (stabilized)", (r) => pctFrac(r.yieldOnCost)],
  ["Development Spread", (r) => (r.devSpread === null ? "—" : `${(r.devSpread * 10000).toFixed(0)} bps`)],
  ["Exit Value", (r) => usd(r.exitValue)],
  ["Unlevered IRR", (r) => pctFrac(r.unleveredIRR)],
  ["Levered IRR", (r) => pctFrac(r.leveredIRR)],
  ["Equity Multiple", (r) => mult(r.equityMultiple)],
];

const delta = (a, b) => (a === null || b === null ? null : (a - b) * 10000);

export default function StressTest({ inputs, base, baseLabel = "Current", onApply }) {
  const cases = useMemo(() => STRESS.map((s) => ({ ...s, r: compute(s.apply(inputs)) })), [inputs]);
  return (
    <Card eyebrow="Downside Cases" title="Stress Tests" testId="stress-test-card">
      <p className="text-sm text-slate-600 mb-4">Each case is applied to the scenario shown as "{baseLabel}" (cases don't stack). Click a case to load it into the calculator.</p>
      <div className="flex flex-wrap gap-2 mb-5">
        {cases.map((c) => (
          <button key={c.id} onClick={() => onApply(c)} title={c.desc} data-testid={`stress-apply-${c.id}`}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-[#E2DCD2] bg-white text-sm hover:border-[#C59B27] hover:bg-[#C59B27]/10 transition-colors">
            <Zap className="h-3.5 w-3.5 text-[#C59B27]" /> {c.label}
          </button>
        ))}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]" data-testid="stress-test-table">
          <thead>
            <tr className="border-b border-[#0F2E23]">
              <th className="text-left py-2 pr-2 text-[#3E5C4E] font-semibold">Metric</th>
              <th className="text-right py-2 px-2 font-display text-base text-[#0F2E23]">{baseLabel}</th>
              {cases.map((c) => <th key={c.id} className="text-right py-2 px-2 font-display text-base text-[#0F2E23]">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {ROWS.map(([label, fn]) => (
              <tr key={label} className="border-b border-[#E2DCD2]">
                <td className="py-2 pr-2 text-slate-700">{label}</td>
                <td className="py-2 px-2 text-right font-mono-num font-semibold text-[#0F2E23]">{fn(base)}</td>
                {cases.map((c) => <td key={c.id} className="py-2 px-2 text-right font-mono-num text-slate-700" data-testid={`stress-${c.id}-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{fn(c.r)}</td>)}
              </tr>
            ))}
            <tr className="bg-[#F9F6F0]">
              <td className="py-2 pr-2 font-semibold text-[#0F2E23]">Δ Levered IRR vs. {baseLabel}</td>
              <td className="py-2 px-2 text-right font-mono-num">—</td>
              {cases.map((c) => {
                const d = delta(c.r.leveredIRR, base.leveredIRR);
                return <td key={c.id} className={`py-2 px-2 text-right font-mono-num font-semibold ${d !== null && d < 0 ? "text-red-700" : "text-emerald-700"}`} data-testid={`stress-${c.id}-delta`}>{d === null ? "n/a" : `${d > 0 ? "+" : ""}${d.toFixed(0)} bps`}</td>;
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
}
